'use client';

/* eslint-disable @next/next/no-img-element -- CRM V2 is a Vite SPA without Next.js image optimization. */

import { Check, ChevronRight, CirclePlus } from 'lucide-react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import type { ReactNode } from 'react';

import { SETUP_PATH_MOTION } from '@/components/setup/experience/constants';
import { cn } from '@/lib/utils';

interface SetupPaymentOptionCardProps {
  title: string;
  description: string;
  logoSrc?: string;
  /** Branded text tile shown when no logo is available (e.g. white-label sandbox). */
  logoFallbackText?: string;
  isSelected: boolean;
  isRecommended?: boolean;
  /** Label shown when this option is the one currently live on the store. */
  activeChip?: string | null;
  disabled?: boolean;
  onSelect: () => void;
  children?: ReactNode;
}

export function SetupPaymentOptionCard({
  title,
  description,
  logoSrc,
  logoFallbackText,
  isSelected,
  isRecommended = false,
  activeChip = null,
  disabled = false,
  onSelect,
  children,
}: SetupPaymentOptionCardProps) {
  const { t } = useTranslation('setup');

  return (
    <motion.li variants={SETUP_PATH_MOTION.option}>
      <button
        type="button"
        role="radio"
        aria-checked={isSelected}
        className={cn(
          'border-border bg-card flex w-full cursor-pointer items-center gap-[1.125rem] rounded-xl border px-5 py-[1.125rem] text-left transition-[border-color,background,box-shadow] duration-150',
          isSelected
            ? 'border-primary bg-[var(--crm-primary-surface)] shadow-[0_0_0_1px_var(--primary)]'
            : 'hover:enabled:border-[color-mix(in_srgb,var(--border)_60%,var(--foreground))]',
          disabled && 'cursor-not-allowed opacity-60',
        )}
        disabled={disabled}
        onClick={onSelect}
      >
        <span
          className={cn(
            'border-border flex h-12 w-16 shrink-0 items-center justify-center overflow-hidden rounded-lg border bg-[color-mix(in_srgb,var(--muted)_35%,var(--card))]',
            !logoSrc && logoFallbackText && 'border-0 bg-[color-mix(in_srgb,var(--primary)_72%,white)]',
          )}
        >
          {logoSrc ? (
            <img src={logoSrc} alt="" className="h-full w-full object-contain p-1.5" draggable={false} />
          ) : logoFallbackText ? (
            <span className="text-center text-[0.625rem] font-semibold uppercase leading-[1.1] tracking-[0.04em] text-white">
              {logoFallbackText}
            </span>
          ) : null}
        </span>

        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
            <span className="text-foreground text-sm font-semibold leading-[1.25]">{title}</span>
            {activeChip ? (
              <span className="bg-primary text-primary-foreground rounded px-[0.4375rem] py-0.5 text-[0.5625rem] font-semibold uppercase tracking-[0.06em]">
                {activeChip}
              </span>
            ) : isRecommended ? (
              <span className="text-primary bg-card rounded border border-[color-mix(in_srgb,var(--primary)_28%,var(--border))] px-[0.4375rem] py-0.5 text-[0.5625rem] font-semibold uppercase tracking-[0.06em]">
                {t('wizard.recommended')}
              </span>
            ) : null}
          </span>
          <span
            className={cn(
              'mt-1 block text-[0.8125rem] leading-[1.45]',
              isSelected
                ? 'text-[color-mix(in_srgb,var(--foreground)_72%,var(--muted-foreground))]'
                : 'text-muted-foreground',
            )}
          >
            {description}
          </span>
        </span>

        <span className="flex shrink-0 items-center justify-center" aria-hidden>
          {isSelected ? (
            <motion.span
              initial={SETUP_PATH_MOTION.radioDot.initial}
              animate={SETUP_PATH_MOTION.radioDot.animate}
              transition={SETUP_PATH_MOTION.radioDot.transition}
              className="bg-primary text-primary-foreground flex h-6 w-6 items-center justify-center rounded-full"
            >
              <Check className="size-3.5" strokeWidth={3} />
            </motion.span>
          ) : (
            <span className="block h-6 w-6 rounded-full border-2 border-[color-mix(in_srgb,var(--border)_80%,var(--foreground))]" />
          )}
        </span>
      </button>
      {children}
    </motion.li>
  );
}

interface SetupPaymentAddCardProps {
  title: string;
  description: string;
  /** Brand logos previewed under the title in place of the description text. */
  providerLogos?: { src: string; alt: string }[];
  /** Trailing label after the logos (e.g. "+30 more"). */
  moreLabel?: string;
  disabled?: boolean;
  onOpen: () => void;
}

export function SetupPaymentAddCard({
  title,
  description,
  providerLogos,
  moreLabel,
  disabled = false,
  onOpen,
}: SetupPaymentAddCardProps) {
  return (
    <motion.li variants={SETUP_PATH_MOTION.option}>
      <button
        type="button"
        className={cn(
          'border-border bg-card flex w-full cursor-pointer items-center gap-[1.125rem] rounded-xl border border-dashed px-5 py-4 text-left transition-[border-color,background] duration-150',
          'hover:enabled:border-[color-mix(in_srgb,var(--border)_60%,var(--foreground))]',
          disabled && 'cursor-not-allowed opacity-60',
        )}
        disabled={disabled}
        onClick={onOpen}
      >
        <span className="border-border flex h-12 w-16 shrink-0 items-center justify-center rounded-lg border border-dashed bg-[color-mix(in_srgb,var(--muted)_35%,var(--card))]">
          <CirclePlus className="text-muted-foreground size-5" strokeWidth={1.75} aria-hidden />
        </span>
        <span className="min-w-0 flex-1">
          <span className="text-foreground block text-sm font-semibold leading-[1.25]">{title}</span>
          {providerLogos?.length ? (
            <span className="mt-1.5 flex flex-wrap items-center gap-1.5" aria-label={description}>
              {providerLogos.map((logo) => (
                <span
                  key={logo.src}
                  className="border-border bg-card flex h-5 w-7 shrink-0 items-center justify-center overflow-hidden rounded-md border"
                >
                  <img
                    src={logo.src}
                    alt={logo.alt}
                    className="h-full w-full object-contain p-0.5"
                    draggable={false}
                  />
                </span>
              ))}
              {moreLabel ? (
                <span className="text-muted-foreground text-xs leading-none">{moreLabel}</span>
              ) : null}
            </span>
          ) : (
            <span className="text-muted-foreground mt-1 block text-[0.8125rem] leading-[1.45]">
              {description}
            </span>
          )}
        </span>
        <span className="flex shrink-0 items-center justify-center" aria-hidden>
          <ChevronRight className="text-muted-foreground size-5" strokeWidth={2} />
        </span>
      </button>
    </motion.li>
  );
}
